/**
 * Scheduling Console - Phase 15.
 *
 * Replaces Amanda's manual MapPoint 2013 / Excel-based scheduling. Renders
 * a week-view calendar: 7 day-columns, today + 6 days forward by default.
 * Each column shows inspection cards stacked vertically, sorted by
 * scheduled time, colour-coded by lifecycle state.
 *
 * Release 1 requirement S13-F001. Ships now: week/month calendar; a
 * custom-styled Map view (server-geocoded inspections, lifecycle
 * markers, selected panel); drag-to-reschedule (drag a card across days
 * - PATCHes scheduled_for, preserving time-of-day); and per-day route
 * optimisation (F005 - Google Routes order + distance/time, surfaced as
 * stop ordinals on the cards). Still to come in follow-up slices:
 *   - Drag-to-assign-FTS (reassign the inspector, not just the day)
 *   - FTS-availability matrix (rows = inspectors, cols = days)
 *   - PJW priority indicator (contractors behind on Inspection Matrix)
 *
 * The PJW priority data is already wired (Phase 11 - Inspection Matrix
 * backend). The frontend hook + UI for it lands in the next session
 * because it requires a separate API endpoint that joins contractor
 * warranty counts with the matrix service.
 */

import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { toast } from 'sonner'
import {
  CalendarDays,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  Filter,
  MapPin,
  ClipboardCheck,
  Route,
  Loader2,
} from 'lucide-react'
import { AppShell } from '@/admin/components/app-shell'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  useSchedulingInspections,
  useInspectionSubTypes,
  useRescheduleInspection,
  useOptimizeRoute,
  useInspectorSearch,
  useInspections,
  useAssignInspection,
  useAutoScheduleInspections,
  useSuggestedInspectors,
  useInspectionChoices,
  type InspectionRow,
  type InspectorOption,
  type RouteOptimizationResult,
} from '@/admin/hooks/use-inspections'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { formatDistanceMeters } from '@/shared/lib/formatting'
import { SearchablePicker } from '@/admin/pages/inspections/searchable-picker'
import { SchedulingMapView } from '@/admin/pages/inspections/scheduling-map-view'

const WEEK_DAYS = 7
const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MAX_CARDS_PER_DAY_IN_MONTH = 2

type ViewMode = 'week' | 'month' | 'map'

// Card border accent per lifecycle state. Matches the badge variants on
// the list page so the calendar visually rhymes with the table.
const STATUS_ACCENT: Record<string, { border: string; bg: string }> = {
  draft: {
    border: 'border-l-[var(--ink-3)]',
    bg: 'bg-[var(--bg-card)]',
  },
  scheduled: {
    border: 'border-l-[var(--status-info,#5a6356)]',
    bg: 'bg-[var(--bg-card)]',
  },
  in_progress: {
    border: 'border-l-[var(--status-in-progress,#6b5c3e)]',
    bg: 'bg-[var(--bg-light)]',
  },
  complete: {
    border: 'border-l-[var(--status-ok,#1c5a48)]',
    bg: 'bg-[var(--bg-card)]',
  },
  repair_pending: {
    border: 'border-l-[var(--status-warn,#92400e)]',
    bg: 'bg-[var(--bg-card)]',
  },
  repair_complete: {
    border: 'border-l-[var(--status-ok,#1c5a48)]',
    bg: 'bg-[var(--bg-card)]',
  },
}

function buildLabelMap(options: { value: string; label: string }[] | undefined): Record<string, string> {
  if (!options) return {}
  return Object.fromEntries(options.map((o) => [o.value, o.label]))
}

/** Format a Date as YYYY-MM-DD (local-zone) for API + grouping keys. */
function isoLocal(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Add N days (positive or negative) to a Date and return a new Date. */
function addDays(date: Date, days: number): Date {
  const out = new Date(date)
  out.setDate(out.getDate() + days)
  return out
}

/**
 * First day of the calendar grid containing the month that ``anchor``
 * sits in. Calendar starts on Sunday - walks back from the 1st of the
 * month to the most recent Sunday so the grid always has 7 columns.
 */
function startOfMonthGrid(anchor: Date): Date {
  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  return addDays(firstOfMonth, -firstOfMonth.getDay())
}

/**
 * Last day of the calendar grid containing the month that ``anchor``
 * sits in. Calendar ends on Saturday - walks forward from the last day
 * of the month to the next Saturday.
 */
function endOfMonthGrid(anchor: Date): Date {
  const lastOfMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)
  return addDays(lastOfMonth, 6 - lastOfMonth.getDay())
}

/** Add N months to a Date, snapping to the 1st of the resulting month. */
function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1)
}

/** Month + year label, e.g. "May 2026". */
function monthYearLabel(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
}

/** Short day name for column header, e.g. "Mon". */
function dayShort(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, { weekday: 'short' })
}

/** Day + month for column header, e.g. "14 May". */
function dayMonth(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' })
}

/** Human time-of-day, e.g. "09:00". Falls back to em-dash for nulls. */
function timeOfDay(value: string | null, locale: string): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/**
 * Move an inspection to ``targetDayKey`` (local YYYY-MM-DD) while
 * preserving its original time-of-day. Inspections with no prior time
 * default to 09:00 local. Returned as an ISO string for the PATCH body.
 */
function rescheduledIso(
  originalIso: string | null,
  targetDayKey: string,
): string {
  const [y, m, d] = targetDayKey.split('-').map(Number)
  const base = originalIso ? new Date(originalIso) : null
  const valid = base && !Number.isNaN(base.getTime())
  const hh = valid ? (base as Date).getHours() : 9
  const mm = valid ? (base as Date).getMinutes() : 0
  return new Date(y, m - 1, d, hh, mm, 0, 0).toISOString()
}

/**
 * Compact route summary, e.g. "12.4 mi · 1h 03m" (en-US) or
 * "20 km · 1h 03m" (fr-FR). Distance unit + number formatting follow
 * the org's locale via `formatDistanceMeters` - never hardcoded.
 */
function routeSummary(
  distanceMeters: number,
  durationSeconds: number,
  locale: string,
): string {
  const dist = formatDistanceMeters(distanceMeters, locale)
  const h = Math.floor(durationSeconds / 3600)
  const min = Math.round((durationSeconds % 3600) / 60)
  const time = h > 0 ? `${h}h ${String(min).padStart(2, '0')}m` : `${min}m`
  return `${dist} · ${time}`
}

function InspectionCard({
  inspection,
  onClick,
  ordinal,
  fromQueue = false,
}: {
  inspection: InspectionRow
  onClick: () => void
  /** 1-based stop number when the day has an optimised route. */
  ordinal?: number
  /** Dragged out of the unassigned queue → drop assigns + schedules. */
  fromQueue?: boolean
}) {
  const { locale } = useFormatting()
  const accent = STATUS_ACCENT[inspection.lifecycle_status] ?? STATUS_ACCENT.draft
  // Claim-triggered inspections are visually distinct (red) and, once
  // scheduled on a day, non-draggable - a claim inspection is not freely
  // reschedulable by drag (Flow C step 3). Unassigned-rail claim cards
  // (fromQueue) stay draggable so drag-to-assign still works.
  const isClaim = inspection.trigger_source === 'from_claim'
  const dragDisabled = isClaim && !fromQueue
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: inspection.uuid,
    data: { inspection, fromQueue },
    disabled: dragDisabled,
  })
  const { t } = useTranslation()
  const accentBorder = isClaim ? 'border-l-[var(--status-error)]' : accent.border
  const accentBg = isClaim ? 'bg-[var(--status-error-bg-subtle)]' : accent.bg
  const cursor = dragDisabled
    ? 'cursor-pointer'
    : 'cursor-grab active:cursor-grabbing'
  return (
    <Button
      ref={setNodeRef}
      type="button"
      variant="ghost"
      onClick={onClick}
      {...attributes}
      {...listeners}
      className={`flex h-auto w-full flex-col items-stretch justify-start gap-1 whitespace-normal rounded-md border-solid border-l-[3px] border-y-[0.5px] border-r-[0.5px] border-y-[var(--rule)] border-r-[var(--rule)] ${accentBorder} ${accentBg} px-2.5 py-2 text-left font-normal transition-colors hover:bg-[var(--bg-light)] ${isDragging ? 'opacity-40' : ''} touch-none ${cursor}`}
    >
      {/* Top row wraps instead of spilling: code + badges only; the time
          lives on the meta line below where narrow columns have room. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] text-[var(--ink-3)]">
          {ordinal != null && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--ink)] px-1 text-[9px] font-medium not-italic leading-none text-[var(--bg-card)]">
              {ordinal}
            </span>
          )}
          {inspection.code}
          {isClaim && (
            <span className="rounded bg-[var(--status-error-bg)] px-1 text-[9px] font-medium not-italic leading-none text-[var(--status-error)]">
              {t('inspections.scheduling.claimBadge', 'Claim')}
            </span>
          )}
          {inspection.itinerary && (
            <span className="rounded bg-[var(--green,#1c5a48)]/10 px-1 text-[9px] font-medium not-italic leading-none text-[var(--green,#1c5a48)]">
              {inspection.itinerary.name}
            </span>
          )}
        </span>
      </div>
      <p className="line-clamp-1 text-xs font-medium text-[var(--ink)]">
        {inspection.sub_type_label}
      </p>
      <p className="line-clamp-1 text-[11px] text-[var(--ink-2)]">
        {inspection.contractor_name || '-'}
      </p>
      {(inspection.property_city || inspection.property_state) && (
        <p className="line-clamp-1 text-[10px] text-[var(--ink-3)]">
          <MapPin size={9} className="inline-block mr-0.5 size-[9px]" />
          {[inspection.property_city, inspection.property_state]
            .filter(Boolean)
            .join(', ')}
        </p>
      )}
      <p className="line-clamp-1 text-[10px] text-[var(--ink-3)]">
        {timeOfDay(inspection.scheduled_for, locale)}
        {inspection.inspector_name
          ? t('inspections.scheduling.ftsSuffix', ' · FTS: {{name}}', {
              name: inspection.inspector_name,
            })
          : ''}
      </p>
    </Button>
  )
}

/**
 * Compact day cell used by the month grid. Smaller than DayColumn -
 * shows only the day number, count badge, up to 2 inspection cards,
 * and a "+N more" link that drills into the week view for that day.
 */
function MonthCellItem({
  inspection,
  onClick,
}: {
  inspection: InspectionRow
  onClick: () => void
}) {
  const { locale } = useFormatting()
  const isClaim = inspection.trigger_source === 'from_claim'
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: inspection.uuid,
    data: { inspection },
    disabled: isClaim,
  })
  const accentBorder = isClaim
    ? 'border-l-[var(--status-error)]'
    : (STATUS_ACCENT[inspection.lifecycle_status]?.border ??
      STATUS_ACCENT.draft.border)
  return (
    <Button
      ref={setNodeRef}
      type="button"
      variant="ghost"
      onClick={onClick}
      {...attributes}
      {...listeners}
      className={`flex h-auto w-full items-center justify-start gap-1 truncate rounded border-solid border-l-[2px] ${accentBorder} ${
        isClaim ? 'bg-[var(--status-error-bg-subtle)]' : 'bg-[var(--bg-card)]'
      } px-1.5 py-0.5 text-left font-normal transition-colors hover:bg-[var(--bg-light)] ${
        isDragging ? 'opacity-40' : ''
      } touch-none ${isClaim ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}`}
    >
      <span className="font-mono text-[9px] text-[var(--ink-3)]">
        {timeOfDay(inspection.scheduled_for, locale)}
      </span>
      <span className="truncate text-[10px] text-[var(--ink)]">
        {inspection.contractor_name || inspection.sub_type_label}
      </span>
    </Button>
  )
}

function MonthCell({
  date,
  inspections,
  isCurrentMonth,
  onCardClick,
  onDrillDown,
}: {
  date: Date
  inspections: InspectionRow[]
  isCurrentMonth: boolean
  onCardClick: (uuid: string) => void
  onDrillDown: (date: Date) => void
}) {
  const dayKey = isoLocal(date)
  const isToday = dayKey === isoLocal(new Date())
  const { setNodeRef, isOver } = useDroppable({ id: dayKey })
  const { t } = useTranslation()
  const visible = inspections.slice(0, MAX_CARDS_PER_DAY_IN_MONTH)
  const hidden = inspections.length - visible.length
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col gap-1 rounded-md border ${
        isOver
          ? 'border-[var(--ink)] ring-2 ring-[var(--ink)]/15'
          : 'border-[var(--rule)]'
      } ${
        isToday
          ? 'bg-[var(--bg-light)]'
          : isCurrentMonth
            ? 'bg-[var(--bg)]'
            : 'bg-[var(--bg)] opacity-50'
      } p-2 min-h-[100px] transition-colors`}
    >
      <div className="flex items-center justify-between gap-1">
        <span
          className={`text-[11px] font-medium ${
            isToday
              ? 'text-[var(--ink)]'
              : isCurrentMonth
                ? 'text-[var(--ink-2)]'
                : 'text-[var(--ink-3)]'
          }`}
        >
          {date.getDate()}
        </span>
        {inspections.length > 0 && (
          <span className="rounded-full bg-[var(--ink)] px-1.5 text-[9px] font-medium leading-[14px] text-white">
            {inspections.length}
          </span>
        )}
      </div>
      {visible.map((inspection) => (
        <MonthCellItem
          key={inspection.uuid}
          inspection={inspection}
          onClick={() => onCardClick(inspection.uuid)}
        />
      ))}
      {hidden > 0 && (
        <Button
          type="button"
          variant="ghost"
          onClick={() => onDrillDown(date)}
          className="h-auto self-start px-0 py-0 text-[10px] font-normal text-[var(--ink-3)] underline-offset-2 hover:bg-transparent hover:text-[var(--ink)] hover:underline"
        >
          {t('inspections.scheduling.moreCount', '+{{count}} more', { count: hidden })}
        </Button>
      )}
    </div>
  )
}

/**
 * Order a day's inspections by an optimised route when one exists:
 * route stops first, in visiting order; anything not in the route
 * (e.g. skipped - no usable address) keeps its original order after.
 */
function applyRouteOrder(
  inspections: InspectionRow[],
  route: RouteOptimizationResult | undefined,
): { row: InspectionRow; ordinal?: number }[] {
  if (!route || route.ordered_inspection_uuids.length === 0) {
    return inspections.map((row) => ({ row }))
  }
  const rank = new Map(
    route.ordered_inspection_uuids.map((u, i) => [u, i]),
  )
  const inRoute = inspections
    .filter((i) => rank.has(i.uuid))
    .sort((a, b) => (rank.get(a.uuid) ?? 0) - (rank.get(b.uuid) ?? 0))
    .map((row, i) => ({ row, ordinal: i + 1 }))
  const rest = inspections
    .filter((i) => !rank.has(i.uuid))
    .map((row) => ({ row }))
  return [...inRoute, ...rest]
}

function DayColumn({
  date,
  inspections,
  onCardClick,
  route,
  routeError,
  optimizing,
  onOptimize,
  onViewOnMap,
  locale,
}: {
  date: Date
  inspections: InspectionRow[]
  onCardClick: (uuid: string) => void
  route?: RouteOptimizationResult
  routeError?: string
  optimizing: boolean
  onOptimize: (dayKey: string, uuids: string[]) => void
  onViewOnMap: (date: Date) => void
  locale: string
}) {
  const dayKey = isoLocal(date)
  const isToday = dayKey === isoLocal(new Date())
  const { setNodeRef, isOver } = useDroppable({ id: dayKey })
  const { t } = useTranslation()
  const ordered = applyRouteOrder(inspections, route)
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col gap-2 rounded-lg border ${
        isOver
          ? 'border-[var(--ink)] ring-2 ring-[var(--ink)]/15'
          : 'border-[var(--rule)]'
      } ${
        isToday ? 'bg-[var(--bg-light)]' : 'bg-[var(--bg)]'
      } p-3 min-h-[420px] transition-colors`}
    >
      <div className="flex items-baseline justify-between border-b-[0.5px] border-[var(--rule)] pb-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {dayShort(date, locale)}
          </p>
          <p
            className={`font-display text-[20px] leading-none ${
              isToday ? 'text-[var(--ink)]' : 'text-[var(--ink-2)]'
            }`}
          >
            {dayMonth(date, locale)}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {inspections.length > 0 && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => onViewOnMap(date)}
              aria-label={t('inspections.scheduling.viewDayOnMapAria', 'View {{day}} on map', {
                day: dayMonth(date, locale),
              })}
              title={t('inspections.scheduling.viewDayOnMapTitle', 'View this day on the map')}
              className="text-[var(--ink-3)]"
            >
              <MapPin size={13} />
            </Button>
          )}
          <Badge variant="outline">{inspections.length}</Badge>
        </div>
      </div>

      {route && (
        <div className="flex items-center gap-1.5 rounded-md bg-[var(--bg-light)] px-2 py-1 text-[10px] text-[var(--ink-2)]">
          <Route size={11} className="shrink-0 text-[var(--ink-3)]" />
          <span className="font-medium">
            {route.distance_meters > 0 || route.duration_seconds > 0
              ? routeSummary(
                  route.distance_meters,
                  route.duration_seconds,
                  locale,
                )
              : t('inspections.scheduling.visitingOrderSet', 'Visiting order set')}
          </span>
          {route.skipped_uuids.length > 0 && (
            <span className="text-[var(--ink-3)]">
              ·{' '}
              {t('inspections.scheduling.skippedCount', '{{count}} skipped', {
                count: route.skipped_uuids.length,
              })}
            </span>
          )}
        </div>
      )}
      {routeError && (
        <p className="rounded-md bg-[var(--bg-light)] px-2 py-1 text-[10px] text-[var(--ink-3)]">
          {routeError}
        </p>
      )}

      {inspections.length === 0 ? (
        <p className="text-[11px] text-[var(--ink-3)] mt-2">
          {t('inspections.scheduling.noInspections', 'No inspections.')}
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {ordered.map(({ row, ordinal }) => (
            <InspectionCard
              key={row.uuid}
              inspection={row}
              ordinal={ordinal}
              onClick={() => onCardClick(row.uuid)}
            />
          ))}
          {inspections.length >= 2 && (
            <Button
              variant="ghost"
              size="xs"
              disabled={optimizing}
              onClick={() =>
                onOptimize(
                  dayKey,
                  inspections.map((i) => i.uuid),
                )
              }
              className="mt-1 self-start text-[var(--ink-3)]"
            >
              {optimizing ? (
                <Loader2 size={12} className="mr-1 animate-spin" />
              ) : (
                <Route size={12} className="mr-1" />
              )}
              {route
                ? t('inspections.scheduling.reoptimiseRoute', 'Re-optimise route')
                : t('inspections.scheduling.optimiseRoute', 'Optimise route')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export function SchedulingConsolePage() {
  const navigate = useNavigate()
  const { can } = usePermissions()
  // Scheduling is Coordinator/Lead work: creating needs inspections.create,
  // assigning an inspector to a slot is an edit.
  const canCreate = can('inspections.create')
  const canAssign =
    can('inspections.edit.global') || can('inspections.edit.acl')
  const { locale } = useFormatting()
  const { t } = useTranslation()
  const { data: choices } = useInspectionChoices()
  const statusLabels = useMemo(() => buildLabelMap(choices?.lifecycle_statuses), [choices])
  const [view, setView] = useState<ViewMode>('week')
  // Anchor day. In week view, this is the leftmost day shown (rolling
  // 7-day window). In month view, anchor's month is rendered as a
  // full calendar grid.
  const [searchParams] = useSearchParams()
  const [anchor, setAnchor] = useState<Date>(() => {
    // Open on the date passed in (e.g. from an inspection's "Open Console"
    // link) so the week-view lands on that inspection, not always today.
    const param = searchParams.get('date')
    const parsed = param ? new Date(`${param}T00:00:00`) : null
    const t = parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date()
    t.setHours(0, 0, 0, 0)
    return t
  })
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [subTypeFilter, setSubTypeFilter] = useState<string>('')
  // Inspector-type filter (Gap 9): narrows the FTS picker candidates to
  // QA / Claims / Both. Empty = all types. Drives both the top filter
  // picker and the assign-dialog picker so a claims-only FTS stays out of
  // QA scheduling and vice versa. Options come from the choices endpoint.
  const [inspectorTypeFilter, setInspectorTypeFilter] = useState<string>('')
  // Per-FTS filter (Phase 0b). The feed already supports ?inspector=.
  const [inspector, setInspector] = useState<{
    uuid: string
    label: string
  } | null>(null)
  const [inspectorSearch, setInspectorSearch] = useState('')
  const { data: inspectorResults, isFetching: inspectorLoading } =
    useInspectorSearch(inspectorSearch, inspectorTypeFilter)
  // Day to scope the Map to when jumping from a calendar day (Phase 0a).
  const [mapDay, setMapDay] = useState<string>('')

  // Range bounds + day list differ per view. Week is a rolling 7-day
  // window; month pads to full Sun-Sat rows so the grid always lines
  // up under day-of-week headers.
  const rangeStart = useMemo(
    () => (view === 'week' ? anchor : startOfMonthGrid(anchor)),
    [view, anchor],
  )
  const rangeEnd = useMemo(
    () =>
      view === 'week'
        ? addDays(anchor, WEEK_DAYS - 1)
        : endOfMonthGrid(anchor),
    [view, anchor],
  )
  const days = useMemo(() => {
    const out: Date[] = []
    const total = Math.round(
      (rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24),
    ) + 1
    for (let i = 0; i < total; i += 1) {
      out.push(addDays(rangeStart, i))
    }
    return out
  }, [rangeStart, rangeEnd])

  const { data: feedRows, isLoading } = useSchedulingInspections({
    from: isoLocal(rangeStart),
    to: isoLocal(rangeEnd),
    status: statusFilter || undefined,
    sub_type: subTypeFilter || undefined,
    inspector: inspector?.uuid || undefined,
  })

  // Trip filter (Smart Schedule): narrow the console to one itinerary.
  // Options derive from the rows in range; filtering is client-side.
  const [itineraryFilter, setItineraryFilter] = useState<string>('')
  const itineraryOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const row of feedRows ?? []) {
      if (row.itinerary) seen.set(row.itinerary.uuid, row.itinerary.name)
    }
    return Array.from(seen, ([uuid, name]) => ({ uuid, name }))
  }, [feedRows])
  const inspections = useMemo(
    () =>
      itineraryFilter
        ? (feedRows ?? []).filter(
            (row) => row.itinerary?.uuid === itineraryFilter,
          )
        : feedRows,
    [feedRows, itineraryFilter],
  )

  const { data: subTypes } = useInspectionSubTypes()

  // ── Drag-to-reschedule + per-day route optimisation ──────────
  const reschedule = useRescheduleInspection()
  const optimize = useOptimizeRoute()
  const [activeInsp, setActiveInsp] = useState<InspectionRow | null>(null)
  const [routeByDay, setRouteByDay] = useState<
    Record<string, RouteOptimizationResult>
  >({})
  const [routeErrByDay, setRouteErrByDay] = useState<Record<string, string>>({})
  const [optimizingDay, setOptimizingDay] = useState<string | null>(null)

  // ── Unassigned queue + drag-to-assign (Phase 1) ──────────────
  const { data: unassigned } = useInspections({
    inspector: 'none',
    scheduled: 'none',
  })
  const assign = useAssignInspection()
  const [pendingAssign, setPendingAssign] = useState<{
    inspection: InspectionRow
    dayKey: string
  } | null>(null)
  const [assignFts, setAssignFts] = useState<{
    uuid: string
    label: string
  } | null>(null)
  const [assignFtsSearch, setAssignFtsSearch] = useState('')
  const { data: assignFtsResults, isFetching: assignFtsLoading } =
    useInspectorSearch(assignFtsSearch, inspectorTypeFilter)
  // Phase 3a: the FTS who cover this inspection's GeoZone. The top
  // suggestion is the *derived* default pick (no state-sync effect) -
  // an explicit pick overrides it; clearing falls back to it.
  const { data: suggestedFts } = useSuggestedInspectors(
    pendingAssign?.inspection.uuid,
  )
  const suggestedTop = suggestedFts?.[0] ?? null
  const effectiveFts =
    assignFts ??
    (suggestedTop
      ? {
          uuid: suggestedTop.uuid,
          label: suggestedTop.full_name || suggestedTop.email,
        }
      : null)

  // ── Bulk multi-day scheduling (routing chunk) ────────────────
  // Select several unassigned inspections, pick one field tech + a start
  // date, and let the server build the optimised multi-day weekday route.
  const autoSchedule = useAutoScheduleInspections()
  const [queueSel, setQueueSel] = useState<Set<string>>(() => new Set())
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkFts, setBulkFts] = useState<{ uuid: string; label: string } | null>(
    null,
  )
  const [bulkFtsSearch, setBulkFtsSearch] = useState('')
  const { data: bulkFtsResults, isFetching: bulkFtsLoading } =
    useInspectorSearch(bulkFtsSearch)
  const [bulkStart, setBulkStart] = useState('')

  function toggleQueueSel(uuid: string, checked: boolean) {
    setQueueSel((prev) => {
      const next = new Set(prev)
      if (checked) next.add(uuid)
      else next.delete(uuid)
      return next
    })
  }

  function resetBulk() {
    setBulkOpen(false)
    setBulkFts(null)
    setBulkFtsSearch('')
    setBulkStart('')
  }

  async function runBulkSchedule() {
    if (!bulkFts || !bulkStart || queueSel.size === 0) return
    try {
      const res = await autoSchedule.mutateAsync({
        inspection_uuids: Array.from(queueSel),
        inspector_uuid: bulkFts.uuid,
        start_date: bulkStart,
      })
      const { scheduled, skipped, days } = res.summary
      const bits = [
        t(
          'inspections.scheduling.bulkScheduledSummary',
          'Scheduled {{scheduled}} across {{count}} days',
          { count: days, scheduled },
        ),
      ]
      if (skipped) {
        bits.push(
          t('inspections.scheduling.skippedCount', '{{count}} skipped', {
            count: skipped,
          }),
        )
      }
      toast.success(bits.join(', '))
      setQueueSel(new Set())
      resetBulk()
    } catch (err: unknown) {
      const res = (
        err as
          | { response?: { status?: number; data?: { message?: string } } }
          | undefined
      )?.response
      const serverMessage = res?.data?.message
      toast.error(
        res?.status === 503
          ? t(
              'inspections.scheduling.routeOptimizationNotConfigured',
              "Route optimisation isn't configured for this environment.",
            )
          : serverMessage ||
              t(
                'inspections.scheduling.bulkScheduleFailed',
                'Could not bulk-schedule the selection.',
              ),
      )
    }
  }

  // A plain-click on a card navigates; only a deliberate 6px drag
  // starts DnD, so the existing onClick → detail-page nav still works.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  const handleDragStart = (e: DragStartEvent) => {
    const insp = e.active.data.current?.inspection as InspectionRow | undefined
    setActiveInsp(insp ?? null)
  }

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveInsp(null)
    const { active, over } = e
    if (!over) return
    const insp = active.data.current?.inspection as InspectionRow | undefined
    if (!insp) return
    const targetKey = String(over.id)
    // Dragged out of the unassigned queue → assign an FTS + schedule
    // (one PATCH) via a confirm dialog rather than a bare reschedule.
    if (active.data.current?.fromQueue) {
      setPendingAssign({ inspection: insp, dayKey: targetKey })
      return
    }
    const sourceKey = insp.scheduled_for
      ? isoLocal(new Date(insp.scheduled_for))
      : null
    if (sourceKey === targetKey) return
    reschedule.mutate({
      uuid: insp.uuid,
      scheduled_for: rescheduledIso(insp.scheduled_for, targetKey),
    })
  }

  const handleOptimize = (dayKey: string, uuids: string[]) => {
    setOptimizingDay(dayKey)
    setRouteErrByDay((m) => {
      const next = { ...m }
      delete next[dayKey]
      return next
    })
    optimize.mutate(uuids, {
      onSuccess: (result) => {
        setRouteByDay((m) => ({ ...m, [dayKey]: result }))
        setOptimizingDay(null)
      },
      onError: (err: unknown) => {
        const status = (
          err as { response?: { status?: number } } | undefined
        )?.response?.status
        setRouteErrByDay((m) => ({
          ...m,
          [dayKey]:
            status === 503
              ? t(
                  'inspections.scheduling.routeOptimizationUnavailableConfig',
                  'Route optimisation isn’t configured for this environment.',
                )
              : t(
                  'inspections.scheduling.routeServiceUnavailable',
                  'Route service is temporarily unavailable.',
                ),
        }))
        setOptimizingDay(null)
      },
    })
  }

  // Group by local-zone YYYY-MM-DD key. In week view this groups the
  // current 7 days; in month view it groups the full grid (35 or 42
  // days). Each bucket is sorted by scheduled time so the most recent
  // appears at the top of the day cell.
  const grouped = useMemo(() => {
    const map = new Map<string, InspectionRow[]>()
    for (const day of days) {
      map.set(isoLocal(day), [])
    }
    for (const insp of inspections ?? []) {
      if (!insp.scheduled_for) continue
      const key = isoLocal(new Date(insp.scheduled_for))
      const bucket = map.get(key)
      if (bucket) bucket.push(insp)
    }
    for (const bucket of map.values()) {
      bucket.sort((a, b) => {
        const at = a.scheduled_for ?? ''
        const bt = b.scheduled_for ?? ''
        return at.localeCompare(bt)
      })
    }
    return map
  }, [days, inspections])

  const totalInRange = inspections?.length ?? 0

  const navPrev = () => {
    setAnchor((a) => (view === 'week' ? addDays(a, -WEEK_DAYS) : addMonths(a, -1)))
  }
  const navNext = () => {
    setAnchor((a) => (view === 'week' ? addDays(a, WEEK_DAYS) : addMonths(a, 1)))
  }
  const navToday = () => {
    const t = new Date()
    t.setHours(0, 0, 0, 0)
    if (view === 'month') {
      setAnchor(new Date(t.getFullYear(), t.getMonth(), 1))
    } else {
      setAnchor(t)
    }
  }
  // Drill from month → week: clicking "+N more" or a day in month
  // mode flips to week view anchored on the clicked day.
  const drillToWeek = (date: Date) => {
    const d = new Date(date)
    d.setHours(0, 0, 0, 0)
    setAnchor(d)
    setView('week')
  }
  // Jump to the Map scoped to a specific calendar day (Phase 0a).
  // Re-anchor to that day first so the feed window (the day's month in
  // map view) always contains it, then hand the day to the map.
  const viewDayOnMap = (date: Date) => {
    const d = new Date(date)
    d.setHours(0, 0, 0, 0)
    setAnchor(d)
    setMapDay(isoLocal(d))
    setView('map')
  }

  const rangeLabel =
    view === 'week'
      ? `${dayMonth(rangeStart, locale)} - ${dayMonth(rangeEnd, locale)}`
      : monthYearLabel(anchor, locale)

  return (
    <AppShell>
      <main className="page-container py-10">
        <header className="flex flex-col gap-4">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-[42px] leading-[1.1] text-[var(--ink)]">
                {t('inspections.scheduling.heading', 'Scheduling Console')}
              </h1>
              <p className="mt-1 text-sm text-[var(--ink-3)]">
                {t('inspections.scheduling.inspectionCount', '{{count}} inspections', {
                  count: totalInRange,
                })}{' '}
                {view === 'map'
                  ? t('inspections.scheduling.rangeSuffixMappedThisMonth', 'mapped this month')
                  : view === 'month'
                    ? t('inspections.scheduling.rangeSuffixThisMonth', 'this month')
                    : t('inspections.scheduling.rangeSuffixThisWeek', 'this week')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/inspections')}
              >
                <ClipboardCheck size={14} className="mr-1.5" />
                {t('inspections.scheduling.listViewButton', 'List view')}
              </Button>
              {canCreate && (
                <Button
                  onClick={() => navigate('/inspections/new')}
                >
                  {t('inspections.scheduling.newInspectionButton', 'New Inspection')}
                </Button>
              )}
            </div>
          </div>

          {/* Filter + week-nav bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] px-4 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <Filter size={14} className="text-[var(--ink-3)]" />
              <Select
                value={statusFilter || 'all'}
                onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
              >
                <SelectTrigger className="h-8 min-w-[160px]">
                  <SelectValue placeholder={t('inspections.scheduling.statusPlaceholder', 'Status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('inspections.scheduling.allOpenStatuses', 'All open statuses')}
                  </SelectItem>
                  {(choices?.lifecycle_statuses ?? []).map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={subTypeFilter || 'all'}
                onValueChange={(v) => setSubTypeFilter(v === 'all' ? '' : v)}
              >
                <SelectTrigger className="h-8 min-w-[200px]">
                  <SelectValue placeholder={t('inspections.scheduling.subTypePlaceholder', 'Sub-type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('inspections.scheduling.allSubTypes', 'All sub-types')}
                  </SelectItem>
                  {(subTypes ?? []).map((st) => (
                    <SelectItem key={st.key} value={st.key}>
                      {st.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {itineraryOptions.length > 0 && (
                <Select
                  value={itineraryFilter || 'all'}
                  onValueChange={(v: string) =>
                    setItineraryFilter(v === 'all' ? '' : v)
                  }
                >
                  <SelectTrigger className="h-8 min-w-[150px]">
                    <SelectValue placeholder={t('inspections.scheduling.tripPlaceholder', 'Trip')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t('inspections.scheduling.allTrips', 'All trips')}
                    </SelectItem>
                    {itineraryOptions.map((it) => (
                      <SelectItem key={it.uuid} value={it.uuid}>
                        {it.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select
                value={inspectorTypeFilter || 'all'}
                onValueChange={(v) => {
                  setInspectorTypeFilter(v === 'all' ? '' : v)
                  // A selected inspector may no longer be eligible for the
                  // new type; clear it so the feed + picker stay consistent.
                  setInspector(null)
                  setInspectorSearch('')
                }}
              >
                <SelectTrigger className="h-8 min-w-[150px]">
                  <SelectValue placeholder={t('inspections.scheduling.inspectorTypePlaceholder', 'Inspector type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('inspections.scheduling.allInspectorTypes', 'All inspector types')}
                  </SelectItem>
                  {(choices?.inspector_types ?? []).map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="w-[200px]">
                <SearchablePicker<InspectorOption>
                  label={t('inspections.scheduling.ftsFilterLabel', 'FTS')}
                  compact
                  search={inspectorSearch}
                  onSearchChange={setInspectorSearch}
                  results={inspectorResults}
                  isLoading={inspectorLoading}
                  selectedUuid={inspector?.uuid ?? ''}
                  selectedLabel={inspector?.label}
                  onSelect={(uuid, label) => setInspector({ uuid, label })}
                  onClear={() => {
                    setInspector(null)
                    setInspectorSearch('')
                  }}
                  getOptionUuid={(o) => o.uuid}
                  getOptionPrimary={(o) => o.full_name || o.email}
                  getOptionSecondary={(o) => o.role || undefined}
                  placeholder={t(
                    'inspections.scheduling.filterByInspectorPlaceholder',
                    'Filter by inspector…',
                  )}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Tabs value={view} onValueChange={(v) => setView(v as ViewMode)}>
                <TabsList variant="pill">
                  <TabsTrigger value="week">
                    {t('inspections.scheduling.weekTab', 'Week')}
                  </TabsTrigger>
                  <TabsTrigger value="month">
                    {t('inspections.scheduling.monthTab', 'Month')}
                  </TabsTrigger>
                  <TabsTrigger value="map">
                    {t('inspections.scheduling.mapTab', 'Map')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Button variant="outline" size="sm" onClick={navPrev}>
                <ChevronLeft size={14} />
              </Button>
              <Button variant="outline" size="sm" onClick={navToday}>
                <CalendarDays size={14} className="mr-1.5" />
                {view === 'week'
                  ? t('inspections.scheduling.thisWeekButton', 'This week')
                  : t('inspections.scheduling.thisMonthButton', 'This month')}
              </Button>
              <Button variant="outline" size="sm" onClick={navNext}>
                <ChevronRight size={14} />
              </Button>
              <span className="ml-2 text-xs text-[var(--ink-3)]">
                {rangeLabel}
              </span>
            </div>
          </div>

          {/* Lifecycle legend */}
          <div className="flex flex-wrap items-center gap-3 text-[10px] text-[var(--ink-3)]">
            {Object.keys(STATUS_ACCENT).map((key) => {
              const accent = STATUS_ACCENT[key] ?? STATUS_ACCENT.draft
              return (
                <span key={key} className="flex items-center gap-1.5">
                  <span
                    className={`block h-3 w-1 ${accent.border.replace('border-l-', 'bg-')}`}
                  />
                  {statusLabels[key] ?? key.replace(/_/g, ' ')}
                </span>
              )
            })}
          </div>
        </header>

        {view === 'map' ? (
          <SchedulingMapView
            inspections={inspections ?? []}
            isLoading={isLoading}
            initialDay={mapDay}
            onSelect={(uuid) => navigate(`/inspections/${uuid}`)}
          />
        ) : isLoading ? (
          <div className="mt-8 flex items-center justify-center py-12 text-[var(--ink-3)]">
            {t('inspections.scheduling.loadingInspections', 'Loading scheduled inspections…')}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {view === 'week' ? (
              <div className="mt-6 flex gap-2">
                {(unassigned ?? []).length > 0 && (
                  <div className="flex min-h-[420px] w-[230px] shrink-0 flex-col gap-2 rounded-lg border border-dashed border-[var(--rule)] bg-[var(--bg)] p-3">
                    <div className="flex items-baseline justify-between border-b-[0.5px] border-[var(--rule)] pb-2">
                      <p className="font-display text-[18px] leading-none text-[var(--ink)]">
                        {t('inspections.scheduling.unassignedHeading', 'Unassigned')}
                      </p>
                      <Badge variant="outline">
                        {(unassigned ?? []).length}
                      </Badge>
                    </div>
                    <p className="text-[10px] leading-snug text-[var(--ink-3)]">
                      {t(
                        'inspections.scheduling.unassignedHelpText',
                        'Drag one onto a day to assign it, or tick several and bulk-schedule them as a multi-day trip.',
                      )}
                    </p>
                    {canAssign && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={queueSel.size === 0}
                        onClick={() => setBulkOpen(true)}
                      >
                        <CalendarPlus size={13} className="mr-1.5" />
                        {t('inspections.scheduling.bulkScheduleButton', 'Bulk schedule ({{count}})', {
                          count: queueSel.size,
                        })}
                      </Button>
                    )}
                    <div className="flex flex-col gap-1.5 overflow-y-auto">
                      {(unassigned ?? []).map((insp) => (
                        <div key={insp.uuid} className="flex items-start gap-1.5">
                          {canAssign && (
                            <Checkbox
                              checked={queueSel.has(insp.uuid)}
                              onCheckedChange={(c) =>
                                toggleQueueSel(insp.uuid, c === true)
                              }
                              aria-label={t(
                                'inspections.scheduling.selectForBulkSchedulingAria',
                                'Select {{code}} for bulk scheduling',
                                { code: insp.code },
                              )}
                              className="mt-2"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <InspectionCard
                              inspection={insp}
                              fromQueue
                              onClick={() =>
                                navigate(`/inspections/${insp.uuid}`)
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-7">
                  {days.map((day) => {
                    const key = isoLocal(day)
                    return (
                      <DayColumn
                        key={key}
                        date={day}
                        inspections={grouped.get(key) ?? []}
                        onCardClick={(uuid) =>
                          navigate(`/inspections/${uuid}`)
                        }
                        route={routeByDay[key]}
                        routeError={routeErrByDay[key]}
                        optimizing={optimizingDay === key}
                        onOptimize={handleOptimize}
                        onViewOnMap={viewDayOnMap}
                        locale={locale}
                      />
                    )
                  })}
                </div>
              </div>
            ) : (
              <div className="mt-6 flex flex-col gap-2">
                <div className="grid grid-cols-7 gap-2 px-1">
                  {DOW_LABELS.map((dow) => (
                    <div
                      key={dow}
                      className="text-center text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]"
                    >
                      {t(`inspections.scheduling.dow${dow}`, dow)}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {days.map((day) => (
                    <MonthCell
                      key={isoLocal(day)}
                      date={day}
                      inspections={grouped.get(isoLocal(day)) ?? []}
                      isCurrentMonth={day.getMonth() === anchor.getMonth()}
                      onCardClick={(uuid) => navigate(`/inspections/${uuid}`)}
                      onDrillDown={drillToWeek}
                    />
                  ))}
                </div>
              </div>
            )}
            <DragOverlay dropAnimation={null}>
              {activeInsp ? (
                <div className="w-[200px] cursor-grabbing rounded-md border-l-[3px] border-y-[0.5px] border-r-[0.5px] border-y-[var(--rule)] border-r-[var(--rule)] border-l-[var(--ink)] bg-[var(--bg-card)] px-2.5 py-2 shadow-[var(--shadow-float)]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-[var(--ink-3)]">
                      {activeInsp.code}
                    </span>
                    <span className="text-[10px] text-[var(--ink-3)]">
                      {timeOfDay(activeInsp.scheduled_for, locale)}
                    </span>
                  </div>
                  <p className="line-clamp-1 text-xs font-medium text-[var(--ink)]">
                    {activeInsp.sub_type_label}
                  </p>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        <Dialog
          open={!!pendingAssign}
          onOpenChange={(o) => {
            if (!o) {
              setPendingAssign(null)
              setAssignFts(null)
              setAssignFtsSearch('')
            }
          }}
        >
          <DialogContent className="max-w-[440px]">
            <DialogHeader>
              <DialogTitle>
                {t('inspections.scheduling.assignInspectionTitle', 'Assign inspection')}
              </DialogTitle>
              {pendingAssign && (
                <DialogDescription>
                  <span className="font-mono text-[var(--ink-2)]">
                    {pendingAssign.inspection.code}
                  </span>{' '}
                  · {pendingAssign.inspection.sub_type_label} →{' '}
                  {dayMonth(new Date(`${pendingAssign.dayKey}T00:00:00`), locale)}
                </DialogDescription>
              )}
            </DialogHeader>
            <SearchablePicker<InspectorOption>
              label={t('inspections.scheduling.inspectorFtsLabel', 'Inspector (FTS)')}
              required
              search={assignFtsSearch}
              onSearchChange={setAssignFtsSearch}
              results={assignFtsResults}
              isLoading={assignFtsLoading}
              selectedUuid={effectiveFts?.uuid ?? ''}
              selectedLabel={effectiveFts?.label}
              onSelect={(uuid, label) => setAssignFts({ uuid, label })}
              onClear={() => {
                setAssignFts(null)
                setAssignFtsSearch('')
              }}
              getOptionUuid={(o) => o.uuid}
              getOptionPrimary={(o) => o.full_name || o.email}
              getOptionSecondary={(o) => o.role || undefined}
              placeholder={t('inspections.scheduling.searchInspectorPlaceholder', 'Search inspector…')}
            />
            {effectiveFts &&
              suggestedFts?.some((s) => s.uuid === effectiveFts.uuid) && (
                <p className="flex items-center gap-1.5 text-[11px] text-[var(--status-ok,#1c5a48)]">
                  <MapPin size={11} className="shrink-0" />
                  {t(
                    'inspections.scheduling.recommendedInspectorNote',
                    "Recommended - covers this inspection's zone",
                  )}
                </p>
              )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setPendingAssign(null)
                  setAssignFts(null)
                  setAssignFtsSearch('')
                }}
              >
                {t('inspections.scheduling.cancelButton', 'Cancel')}
              </Button>
              <Button
                disabled={!effectiveFts || assign.isPending || !canAssign}
                onClick={() => {
                  if (!pendingAssign || !effectiveFts || !canAssign) return
                  assign.mutate(
                    {
                      uuid: pendingAssign.inspection.uuid,
                      inspector_uuid: effectiveFts.uuid,
                      scheduled_for: rescheduledIso(
                        pendingAssign.inspection.scheduled_for,
                        pendingAssign.dayKey,
                      ),
                    },
                    {
                      onSuccess: () => {
                        setPendingAssign(null)
                        setAssignFts(null)
                        setAssignFtsSearch('')
                      },
                    },
                  )
                }}
              >
                {assign.isPending
                  ? t('inspections.scheduling.assigningEllipsis', 'Assigning…')
                  : t('inspections.scheduling.assignAndScheduleButton', 'Assign + schedule')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk multi-day scheduling (routing chunk) */}
        <Dialog
          open={bulkOpen}
          onOpenChange={(o) => {
            if (!o) resetBulk()
            else setBulkOpen(true)
          }}
        >
          <DialogContent className="max-w-[440px]">
            <DialogHeader>
              <DialogTitle>
                {t('inspections.scheduling.bulkScheduleTripTitle', 'Bulk schedule a trip')}
              </DialogTitle>
              <DialogDescription>
                {t(
                  'inspections.scheduling.bulkScheduleDescription',
                  '{{count}} inspections will be optimised into a multi-day route and scheduled onto consecutive weekdays from the start date, all assigned to the chosen field tech. Homes with no usable address are skipped.',
                  { count: queueSel.size },
                )}
              </DialogDescription>
            </DialogHeader>
            <SearchablePicker<InspectorOption>
              label={t('inspections.scheduling.fieldTechFtsLabel', 'Field tech (FTS)')}
              required
              search={bulkFtsSearch}
              onSearchChange={setBulkFtsSearch}
              results={bulkFtsResults}
              isLoading={bulkFtsLoading}
              selectedUuid={bulkFts?.uuid ?? ''}
              selectedLabel={bulkFts?.label}
              onSelect={(uuid, label) => setBulkFts({ uuid, label })}
              onClear={() => {
                setBulkFts(null)
                setBulkFtsSearch('')
              }}
              getOptionUuid={(o) => o.uuid}
              getOptionPrimary={(o) => o.full_name || o.email}
              getOptionSecondary={(o) => o.role || undefined}
              placeholder={t('inspections.scheduling.searchFieldTechPlaceholder', 'Search field tech…')}
            />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bulk-start-date" className="text-[12px]">
                {t('inspections.scheduling.tripStartDateLabel', 'Trip start date')}
              </Label>
              <Input
                id="bulk-start-date"
                type="date"
                value={bulkStart}
                onChange={(e) => setBulkStart(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={resetBulk}
                disabled={autoSchedule.isPending}
              >
                {t('inspections.scheduling.cancelButton', 'Cancel')}
              </Button>
              <Button
                disabled={
                  !bulkFts ||
                  !bulkStart ||
                  queueSel.size === 0 ||
                  autoSchedule.isPending ||
                  !canAssign
                }
                onClick={runBulkSchedule}
              >
                {autoSchedule.isPending ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <CalendarPlus size={14} className="mr-1.5" />
                )}
                {t('inspections.scheduling.scheduleInspectionsButton', 'Schedule {{count}} inspections', {
                  count: queueSel.size,
                })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </AppShell>
  )
}
